/**
 * Self-Audit Reflection — Sprint 17c Package 3
 *
 * After Pass 2 generates entities and opportunities, a fast-tier (Haiku) audit
 * checks for volume bias, source coverage gaps, and opportunity diversity.
 * Adjusts confidence scores and suggests missing opportunities.
 */
import { z } from 'zod';
import { extractJSON } from './pass2-schema';
// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------
export const AuditIssueSchema = z.object({
    type: z.enum(['volume_bias', 'missing_source', 'category_concentration', 'low_diversity']),
    entityName: z.string().optional(),
    source: z.enum(['financial', 'calendar', 'email', 'browsing']).optional(),
    reason: z.string(),
    suggestedConfidenceAdjustment: z.number().min(-1).max(1).optional(),
});
export const AuditMissingOpportunitySchema = z.object({
    entityName: z.string(),
    suggestedMission: z.string(),
    agentType: z.enum(['restaurant', 'travel', 'content', 'shopping', 'events', 'financial']),
    reason: z.string(),
});
export const AuditResponseSchema = z.object({
    issues: z.array(AuditIssueSchema).default([]),
    missingOpportunities: z.array(AuditMissingOpportunitySchema).default([]),
    verdict: z.enum(['pass', 'issues_found']),
});
export function emptyAuditMetrics() {
    return {
        auditRun: false,
        auditVerdict: undefined,
        auditIssueCount: 0,
        auditMissingOpportunities: 0,
        auditDurationMs: 0,
    };
}
// ---------------------------------------------------------------------------
// Prompt
// ---------------------------------------------------------------------------
export function buildAuditPrompt(sourceSummaries, entities, opportunities, connectedSources) {
    const entityList = entities.map(e => `- ${e.name} (${e.category}, confidence: ${e.confidence}, status: ${e.status})`).join('\n');
    const oppList = opportunities.map(o => `- ${o.entityName}: "${o.suggestedMission}" (${o.agentType})`).join('\n');
    return `You are auditing an AI analysis for quality. Given:

1. SOURCE DATA SUMMARIES:
${sourceSummaries}

2. GENERATED ENTITIES:
${entityList}

3. GENERATED OPPORTUNITIES:
${oppList}

4. CONNECTED SOURCES: ${connectedSources.join(', ')}

Check for these issues:
- SOURCE COVERAGE: Does every connected data source contribute at least one entity?
  If a source is completely unrepresented, flag it.
- VOLUME BIAS: Are any entities ranked high ONLY because of data volume, not intent?
  (e.g., newsletter subscriptions ranked above booking confirmations)
- CATEGORY CONCENTRATION: Are >60% of entities in the same category? Flag if so.
- OPPORTUNITY DIVERSITY: Do opportunities use at least 2 different agent types?
- UNDERREPRESENTED SIGNALS: Were any high-intent signals (intentLevel 4-5) from
  the source summaries ignored in the final output?

Return JSON:
{
  "issues": [
    { "type": "volume_bias", "entityName": "[entity name from above]", "reason": "...", "suggestedConfidenceAdjustment": -0.2 },
    { "type": "missing_source", "source": "financial", "reason": "..." }
  ],
  "missingOpportunities": [
    { "entityName": "[entity name from above]", "suggestedMission": "...", "agentType": "restaurant", "reason": "..." }
  ],
  "verdict": "pass" or "issues_found"
}`;
}
// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------
export function parseAuditResponse(raw) {
    try {
        const jsonStr = extractJSON(raw);
        const parsed = JSON.parse(jsonStr);
        const result = AuditResponseSchema.safeParse(parsed);
        if (result.success)
            return result.data;
    }
    catch {
        // Parse failure — return null for graceful skip
    }
    return null;
}
// ---------------------------------------------------------------------------
// Applier
// ---------------------------------------------------------------------------
/**
 * Apply audit adjustments to Pass 2 output.
 * - Confidence adjustments from volume_bias issues
 * - Missing opportunities appended
 */
export function applyAuditAdjustments(entities, opportunities, audit) {
    // Apply confidence adjustments
    const adjustedEntities = entities.map(e => {
        const issue = audit.issues.find(i => i.type === 'volume_bias' && i.entityName === e.name && i.suggestedConfidenceAdjustment);
        if (issue && issue.suggestedConfidenceAdjustment) {
            const adjusted = Math.max(0, Math.min(1, e.confidence + issue.suggestedConfidenceAdjustment));
            return { ...e, confidence: adjusted };
        }
        return e;
    });
    // Append missing opportunities
    const additionalOpportunities = audit.missingOpportunities.map(mo => ({
        entityName: mo.entityName,
        description: mo.reason,
        suggestedMission: mo.suggestedMission,
        agentType: mo.agentType,
        context: {},
        createdAt: Date.now(),
        status: 'pending',
    }));
    return { adjustedEntities, additionalOpportunities };
}
//# sourceMappingURL=self-audit.js.map